import React, { useCallback, useState } from 'react';
import { Input } from '../../../components/common/Form';
import { STATES } from '../../../constants/StatesConstants';
import AddressAutocomplete from './AddressAutocomplete';

export interface BasicInfoData {
  restaurantName: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
}

interface LocationBasicInfoStepProps {
  data: BasicInfoData;
  onChange: (data: BasicInfoData) => void;
  onNext: () => void;
  onCancel: () => void;
}

const INPUT_OVERRIDE = `
  .location-wizard-form input,
  .location-wizard-form select {
    padding: 10px 12px !important;
    font-size: 0.8125rem !important;
    height: 40px !important;
    box-sizing: border-box !important;
  }
`;

const LocationBasicInfoStep = ({ data, onChange, onNext, onCancel }: LocationBasicInfoStepProps) => {
  const [errors, setErrors] = useState<Partial<Record<keyof BasicInfoData, string>>>({});
  const [selectedAddress, setSelectedAddress] = useState<{
    streetAddress1: string;
    city: string;
    state: string;
    zip: string;
  } | null>(null);

  const addressMatchesSuggestion =
    selectedAddress !== null &&
    data.streetAddress1.trim().toLowerCase() === selectedAddress.streetAddress1.trim().toLowerCase() &&
    data.city.trim().toLowerCase() === selectedAddress.city.trim().toLowerCase() &&
    data.state.trim().toLowerCase() === selectedAddress.state.trim().toLowerCase() &&
    data.zip.trim() === selectedAddress.zip.trim();

  const validateField = (field: keyof BasicInfoData, value: string): string => {
    if (['restaurantName', 'streetAddress1', 'city', 'state', 'zip', 'email'].includes(field)) {
      if (!value.trim()) return 'This field is required.';
    }

    if (field === 'phone') {
      if (!value.trim()) return 'This field is required.';
      if (value.trim().length < 7) return 'Phone number must be at least 7 characters.';
    }

    if (field === 'email' && value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      return 'Enter a valid email address.';
    }

    return '';
  };

  const validate = (): boolean => {
    const requiredFields: (keyof BasicInfoData)[] = [
      'restaurantName',
      'streetAddress1',
      'city',
      'state',
      'zip',
      'phone',
      'email'
    ];

    const nextErrors: Partial<Record<keyof BasicInfoData, string>> = {};

    requiredFields.forEach((field) => {
      const error = validateField(field, data[field]);
      if (error) nextErrors[field] = error;
    });

    if (!addressMatchesSuggestion) {
      nextErrors.streetAddress1 = 'Please select an address from the suggestions.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  const updateField = (field: keyof BasicInfoData, value: string) => {
    onChange({ ...data, [field]: value });

    setErrors((previous) => {
      const next = { ...previous };
      const error = validateField(field, value);

      if (error) next[field] = error;
      else delete next[field];

      return next;
    });
  };

  const handleAddressSelect = useCallback(
    (address: { streetAddress1: string; city: string; state: string; zip: string }) => {
      const matchingState = STATES.find(
        (state: { label: string; value: string }) => state.value === address.state || state.label === address.state
      );

      const normalizedAddress = {
        streetAddress1: address.streetAddress1,
        city: address.city,
        state: matchingState?.value ?? address.state,
        zip: address.zip
      };

      setSelectedAddress(normalizedAddress);
      onChange({ ...data, ...normalizedAddress });
    },
    [data, onChange]
  );

  return (
    <div className="location-wizard-form" style={{ width: '100%' }}>
      <style>{INPUT_OVERRIDE}</style>

      <Input
        name="restaurantName"
        label="RESTAURANT NAME"
        value={data.restaurantName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('restaurantName', e.target.value)}
        touched={!!errors.restaurantName}
        error={errors.restaurantName}
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '32px',
          marginTop: '8px'
        }}
      >
        <AddressAutocomplete
          value={data.streetAddress1}
          onChange={(value) => updateField('streetAddress1', value)}
          onAddressSelect={handleAddressSelect}
          error={errors.streetAddress1}
        />

        <Input
          name="streetAddress2"
          label="STREET ADDRESS 2"
          value={data.streetAddress2}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('streetAddress2', e.target.value)}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: '32px',
          marginTop: '8px'
        }}
      >
        <Input
          name="city"
          label="CITY"
          value={data.city}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('city', e.target.value)}
          touched={!!errors.city}
          error={errors.city}
        />

        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="font-semibold text-sm tracking-[1.1375px]" htmlFor="state">
            STATE
          </label>
          <div className="mt-2">
            <select
              id="state"
              name="state"
              value={data.state}
              onChange={(e) => updateField('state', e.target.value)}
              className={`w-full text-sm rounded-sm border ${errors.state ? 'border-red-600' : 'border-neutral-400'}`}
              style={{
                padding: '10px 12px',
                fontSize: '0.8125rem',
                borderRadius: '2px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select</option>
              {STATES.map((state: { label: string; value: string }) => (
                <option value={state.value} key={state.value}>
                  {state.label}
                </option>
              ))}
            </select>

            {errors.state && (
              <p role="alert" className="mt-1 text-sm text-red-600">
                {errors.state}
              </p>
            )}
          </div>
        </div>

        <Input
          name="zip"
          label="ZIP"
          value={data.zip}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('zip', e.target.value)}
          touched={!!errors.zip}
          error={errors.zip}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '32px',
          marginTop: '8px'
        }}
      >
        <Input
          name="phone"
          label="PHONE NUMBER"
          value={data.phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('phone', e.target.value)}
          touched={!!errors.phone}
          error={errors.phone}
        />

        <Input
          name="email"
          label="EMAIL ADDRESS"
          value={data.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('email', e.target.value)}
          touched={!!errors.email}
          error={errors.email}
        />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '8px'
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          style={{
            width: '167px',
            height: '35px',
            backgroundColor: 'var(--color-danger)',
            color: 'white',
            borderRadius: '999px',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '1.4px',
            cursor: 'pointer'
          }}
        >
          CANCEL
        </button>

        <button
          type="button"
          onClick={handleNext}
          style={{
            width: '167px',
            height: '35px',
            backgroundColor: 'var(--color-accent)',
            color: 'white',
            borderRadius: '999px',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '1.4px',
            cursor: 'pointer'
          }}
        >
          NEXT
        </button>
      </div>
    </div>
  );
};

export default LocationBasicInfoStep;
